import {z} from 'zod';
import {targetTupleSchema} from '../target';
import {staggerConfigSchema} from '../timeline/timing';
import {SCROLL_POSITION_UNITS} from './scrollUnits';
import type {ScrollPositionUnit} from './scrollUnits';

const scrollPositionKeywordSchema = z.enum([
  'top',
  'center',
  'bottom',
  'left',
  'right',
]);
export type ScrollPositionKeyword = z.infer<typeof scrollPositionKeywordSchema>;

/** A number with an optional unit, e.g. `50`, `-20px`, `75%`, `3vh`, `2rem`. */
export type ScrollPositionNumeric =
  | `${number}`
  | `${number}${ScrollPositionUnit}`;
/** What a position half can start with, before any `+=`/`-=` offset. */
export type ScrollPositionBase = ScrollPositionKeyword | ScrollPositionNumeric;

// Both halves of a position share one grammar: a base, optionally followed by a
// signed offset. Units are accepted on either side — GSAP understands only
// keywords, `%` and pixels, so the runtime resolves `vh`/`vw`/`rem` to pixels
// before GSAP ever parses the string (see `scrollUnits.ts`).
//
//   base   = top|center|bottom|left|right | -?N | -?N<unit>
//   offset = (+=|-=) N<unit>?
const NUMBER_SOURCE = '\\d+(?:\\.\\d+)?';
const UNIT_SOURCE = SCROLL_POSITION_UNITS.join('|');
const KEYWORD_SOURCE = scrollPositionKeywordSchema.options.join('|');
const scrollPositionValuePattern = new RegExp(
  `^((?:${KEYWORD_SOURCE})|(?:-?${NUMBER_SOURCE}(?:${UNIT_SOURCE})?))` +
    `((?:[+-]=${NUMBER_SOURCE}(?:${UNIT_SOURCE})?))?$`
);

export const scrollPositionValueSchema = z.string().refine(
  (val) => {
    const match = scrollPositionValuePattern.exec(val);
    if (!match) return false;

    // GSAP's `_offsetToPx` looks for the FIRST `%` in the token to decide
    // whether the offset is relative, so a `%` offset behind a `%` base is
    // silently read as pixels (`50%-=20%` means "50% minus 20px"). Reject it
    // rather than let a wrong animation ship. A `%` offset on a keyword base is
    // fine, and so is a pixel offset on a `%` base.
    const [, base = '', offset = ''] = match;
    if (base.endsWith('%') && offset.endsWith('%')) return false;

    return true;
  },
  {
    message:
      'ScrollPositionValue must be a keyword (top/center/bottom/left/right), ' +
      'a number with an optional unit (50, -20px, 75%, 3vh), or either of ' +
      'those followed by an offset (top+=50, center-=20%, bottom-=3vh). ' +
      `Units: ${SCROLL_POSITION_UNITS.join(', ')}. A % offset cannot follow a ` +
      '% base — GSAP would resolve it as pixels.',
  }
) as z.ZodType<
  | ScrollPositionBase
  | `${ScrollPositionBase}+=${ScrollPositionNumeric}`
  | `${ScrollPositionBase}-=${ScrollPositionNumeric}`
>;
export type ScrollPositionValue = z.infer<typeof scrollPositionValueSchema>;

export const scrollPositionSchema = z.string().refine(
  (val) => {
    // Must be two space-separated values
    const parts = val.split(' ');
    if (parts.length !== 2) return false;

    // Each part must be a valid ScrollPositionValue
    return parts.every(
      (part) => scrollPositionValueSchema.safeParse(part).success
    );
  },
  {
    message:
      'ScrollPosition must be two space-separated ScrollPositionValues (e.g., "top center", "50% bottom", "top+=100 bottom", "bottom top-=3vh", "100 -50")',
  }
) as z.ZodType<`${string} ${string}`>;
// Deliberately NOT `${ScrollPositionValue} ${ScrollPositionValue}`: that squares
// the value union (~143 members) into ~20k, and TypeScript then reports TS2590
// ("union type too complex to represent") on ordinary `as ScrollPosition`
// assertions. All this type needs to carry is "two space-separated tokens" —
// the refine above is what actually validates each half.
export type ScrollPosition = z.infer<typeof scrollPositionSchema>;

export const scrollToggleActionSchema = z.enum([
  'play',
  'pause',
  'resume',
  'reverse',
  'restart',
  'reset',
  'complete',
  'none',
]);
export type ScrollToggleAction = z.infer<typeof scrollToggleActionSchema>;

export const scrollTriggerConfigSchema = z.object({
  /**
   * Set by the split Scroll scrub / Scroll actions / Scroll direction editors
   * (and the store when it creates those triggers). The runtime takes the
   * split-era code paths — pin options, `vh`/`vw`/`rem` positions, out-of-view
   * reset, batch stagger, Scroll direction — only when it's present, so a
   * config that has never been through those editors keeps its published
   * behavior exactly. The runtime can't key off the trigger type: it is
   * plugin-agnostic, and legacy `wf:scroll` interactions edited with the split
   * on go through the same editors without a key migration.
   */
  split: z.literal(true).optional(),
  /**
   * Whether to show debug markers in designer (always hidden in production)
   */
  showMarkers: z.boolean().optional(),
  /**
   * If scroll start and end values are not clamped, and they appear above the fold or in the footer of a site,
   * the scroll animation will either start partially scrubbed or never completely finish. However in some cases,
   * this makes sense to turn off.
   */
  clamp: z.boolean().optional(),
  /**
   * Position where the ScrollTrigger starts
   * Maps to the visual position selectors and percentage inputs
   * Examples: "top center", "top 80%", "center bottom"
   */
  start: scrollPositionSchema,
  /**
   * Position where the ScrollTrigger ends
   * Maps to the second set of visual position selectors
   * Examples: "bottom top", "center 20%"
   */
  end: scrollPositionSchema,
  /**
   * Scrub behavior - maps to the toggle and smoothness slider
   * - false/undefined: no scrubbing (toggle off)
   * - true: 1:1 scrubbing (toggle on, smoothness at 0)
   * - number: smoothing duration in seconds (toggle on, smoothness slider value)
   */
  scrub: z.number().nonnegative().nullable().optional(),
  /**
   * These map to the four dropdown menus in the Controls section
   * They can be stored separately for the UI but combined for GSAP
   */
  enter: scrollToggleActionSchema.optional(),
  leave: scrollToggleActionSchema.optional(),
  enterBack: scrollToggleActionSchema.optional(),
  leaveBack: scrollToggleActionSchema.optional(),
  /**
   * Scroll direction only. By default (undefined/false) every direction change
   * inside the Start→End range plays that direction's actions — the "hide on
   * scroll down, show on any scroll up" header pattern. When true, direction
   * changes inside the range are ignored and actions play only at its edges:
   * scroll down actions when crossing Start going down, and scroll up actions
   * when crossing End going up.
   */
  directionEdgesOnly: z.boolean().optional(),
  /**
   * Which scroll direction plays the timeline forward. 'down' (or undefined)
   * is the only polarity the runtime builds today: scrolling down plays,
   * scrolling up reverses. ('up' is
   * reserved — a Scroll direction interaction with a separate reverse group
   * expresses the upward half as its own authored timeline, not as a mirrored
   * config; see `AnimationCoordinator.setupScrollDirectionPair` in #122802.)
   *
   * Its *presence* carries a second meaning: the Scroll direction trigger seeds
   * it at creation (even as the no-op 'down'), so it doubles as the marker for
   * "this config came from Scroll direction". The runtime builds GSAP vars from
   * `scrollTriggerConfig` alone and never sees the extension key, and no other
   * field combination is unique to that trigger.
   */
  scrollDirection: z.enum(['down', 'up']).optional(),
  /**
   * Resets the timeline to progress 0 (paused) once the element scrolls fully
   * out of the viewport, re-arming it for another play. Directionally
   * conditional based on which edge the element exits:
   * - 'scrollingDown': reset only when it leaves via the top (user scrolled down)
   * - 'scrollingUp': reset only when it leaves via the bottom (user scrolled up)
   * - 'both': reset on either exit
   * - 'none'/undefined: no out-of-view reset
   * Backed by a persistent IntersectionObserver at runtime. Only applies to
   * toggle-action (non-scrub) scroll triggers.
   */
  outOfViewReset: z
    .enum(['none', 'scrollingDown', 'scrollingUp', 'both'])
    .optional(),
  /**
   * Batch stagger config for elements that enter the viewport together, for
   * multi-element scroll-action triggers whose action targets are resolved
   * relative to the trigger element. When an amount/each is set, the runtime
   * groups the triggers via ScrollTrigger.batch() and offsets each element's
   * reveal by a per-element delay derived from this config (via
   * gsap.utils.distribute), honoring from/each-vs-amount/grid/axis/ease — a
   * cascade instead of a simultaneous reveal. Undefined (or no amount/each)
   * preserves the existing simultaneous behavior. Only applies to toggle-action
   * (non-scrub) scroll triggers; ignored when scrubbing.
   */
  stagger: staggerConfigSchema.optional(),
  /**
   * Pin the trigger element during scroll
   * - false: no pinning (checkbox unchecked)
   * - true: pin the trigger element (checkbox checked)
   * - TargetDefinition: pin a different element (advanced option)
   */
  pin: z.union([z.boolean(), targetTupleSchema]).optional(),
  /**
   * Whether GSAP reserves scroll space for the pinned element (its pin-spacer).
   * true/undefined: content below is pushed down for the pin's duration (GSAP's
   * default). false: no spacer, so content underneath scrolls up behind the
   * pinned element. Two deliberate visual results, not a default and an edge
   * case. Only meaningful when `pin` is truthy.
   */
  pinSpacing: z.boolean().optional(),
  /**
   * Horizontal scrolling mode (likely in advanced options)
   */
  horizontal: z.boolean().optional(),
  /**
   * Alternative element to use for end position calculations
   * Shown when clicking the element selector button in end section
   */
  endTrigger: targetTupleSchema.optional(),
  /**
   * The scrollable element (visible when advanced options expanded)
   * Default is viewport, but can be set to a container selector
   */
  scroller: targetTupleSchema.optional(),
});
export type ScrollTriggerConfig = z.infer<typeof scrollTriggerConfigSchema>;
export type OutOfViewReset = NonNullable<ScrollTriggerConfig['outOfViewReset']>;
